/**
 * Luqta SDK UI Styles
 * Pre-configured UI styling system with customizable branding
 */

import { BrandingConfig } from '../types';

/**
 * Default branding configuration
 */
export const DEFAULT_BRANDING: Required<BrandingConfig> = {
  primaryColor: '#5304fb',
  secondaryColor: '#8f67fd',
  backgroundColor: '#ffffff',
  textColor: '#111827',
  logoUrl: '',
  appName: 'Luqta',
  borderRadius: 12,
  fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
};

/**
 * Generates CSS variables from branding config
 */
export function generateCSSVariables(branding: BrandingConfig): string {
  const config = { ...DEFAULT_BRANDING, ...branding };

  // Detect if dark mode based on background luminance
  const isDark = isColorDark(config.backgroundColor);

  // Generate adaptive colors based on theme
  const bgLight = isDark ? hexToRgba(config.textColor, 0.05) : hexToRgba(config.textColor, 0.03);
  const bgMedium = isDark ? hexToRgba(config.textColor, 0.1) : hexToRgba(config.textColor, 0.05);
  const bgHover = isDark ? hexToRgba(config.textColor, 0.08) : hexToRgba(config.textColor, 0.04);
  const textLight = hexToRgba(config.textColor, 0.4);
  const overlay = isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.5)';
  const overlayLight = isDark ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.3)';

  // Badge/chip backgrounds - for elements on cards (not on images)
  // These adapt to the main background theme
  const badgeBg = isDark ? hexToRgba(config.textColor, 0.12) : hexToRgba(config.textColor, 0.08);
  const badgeBgLight = isDark ? hexToRgba(config.textColor, 0.08) : hexToRgba(config.textColor, 0.05);
  const badgeBgMedium = isDark ? hexToRgba(config.textColor, 0.15) : hexToRgba(config.textColor, 0.1);

  // Image overlay badges - always dark with blur for visibility on any image
  const imageBadgeBg = 'rgba(0, 0, 0, 0.6)';
  const imageBadgeText = '#ffffff';

  // Inverted colors for dark/light theme
  const invertedBorder = isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.1)';
  const invertedBorderStrong = isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.2)';

  return `
    --luqta-primary: ${config.primaryColor};
    --luqta-primary-light: ${hexToRgba(config.primaryColor, 0.1)};
    --luqta-primary-medium: ${hexToRgba(config.primaryColor, 0.2)};
    --luqta-primary-shadow: ${hexToRgba(config.primaryColor, 0.3)};
    --luqta-secondary: ${config.secondaryColor};
    --luqta-bg: ${config.backgroundColor};
    --luqta-bg-light: ${bgLight};
    --luqta-bg-medium: ${bgMedium};
    --luqta-bg-hover: ${bgHover};
    --luqta-text: ${config.textColor};
    --luqta-text-muted: ${hexToRgba(config.textColor, 0.6)};
    --luqta-text-light: ${textLight};
    --luqta-border: ${hexToRgba(config.textColor, 0.1)};
    --luqta-border-medium: ${hexToRgba(config.textColor, 0.2)};
    --luqta-overlay: ${overlay};
    --luqta-overlay-light: ${overlayLight};
    --luqta-badge-bg: ${badgeBg};
    --luqta-badge-bg-light: ${badgeBgLight};
    --luqta-badge-bg-medium: ${badgeBgMedium};
    --luqta-inverted-border: ${invertedBorder};
    --luqta-inverted-border-strong: ${invertedBorderStrong};
    --luqta-image-badge-bg: ${imageBadgeBg};
    --luqta-image-badge-text: ${imageBadgeText};
    --luqta-radius: ${config.borderRadius}px;
    --luqta-radius-sm: ${Math.max(4, config.borderRadius - 4)}px;
    --luqta-radius-lg: ${config.borderRadius + 4}px;
    --luqta-font: ${config.fontFamily};
    --luqta-success: #10b981;
    --luqta-success-light: rgba(16, 185, 129, 0.1);
    --luqta-success-lighter: rgba(16, 185, 129, 0.05);
    --luqta-success-dark: #059669;
    --luqta-success-shadow: rgba(16, 185, 129, 0.3);
    --luqta-warning: #f59e0b;
    --luqta-warning-light: rgba(245, 158, 11, 0.1);
    --luqta-warning-lighter: rgba(245, 158, 11, 0.05);
    --luqta-warning-dark: #d97706;
    --luqta-warning-bg: #fef3c7;
    --luqta-error: #ef4444;
    --luqta-error-light: rgba(239, 68, 68, 0.1);
    --luqta-error-dark: #dc2626;
    --luqta-info: #3b82f6;
    --luqta-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    --luqta-shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  `;
}

/**
 * Main SDK styles
 */
export const SDK_STYLES = `
  .luqta-sdk {
    font-family: var(--luqta-font);
    background: var(--luqta-bg);
    color: var(--luqta-text);
    min-height: 100%;
    position: relative;
  }

  .luqta-sdk * {
    box-sizing: border-box;
  }

  /* Loading Spinner */
  .luqta-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    gap: 16px;
  }

  .luqta-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid var(--luqta-border);
    border-top-color: var(--luqta-primary);
    border-radius: 50%;
    animation: luqta-spin 0.8s linear infinite;
  }

  @keyframes luqta-spin {
    to { transform: rotate(360deg); }
  }

  /* Header */
  .luqta-header {
    padding: 20px;
    border-bottom: 1px solid var(--luqta-border);
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .luqta-header-logo {
    width: 40px;
    height: 40px;
    border-radius: var(--luqta-radius-sm);
    object-fit: contain;
  }

  .luqta-header-title {
    font-size: 20px;
    font-weight: 600;
    margin: 0;
  }

  /* Navigation */
  .luqta-nav {
    display: flex;
    gap: 8px;
    padding: 16px 20px;
    border-bottom: 1px solid var(--luqta-border);
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .luqta-nav::-webkit-scrollbar {
    display: none;
  }

  .luqta-nav-btn {
    padding: 10px 20px;
    border: none;
    background: transparent;
    color: var(--luqta-text-muted);
    cursor: pointer;
    border-radius: var(--luqta-radius);
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
    transition: all 0.2s ease;
    font-family: inherit;
  }

  .luqta-nav-btn:hover {
    background: var(--luqta-primary-light);
    color: var(--luqta-primary);
  }

  .luqta-nav-btn.active {
    background: var(--luqta-primary);
    color: white;
  }

  /* Content Area */
  .luqta-content {
    padding: 20px;
  }

  .luqta-section-title {
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 16px;
  }

  /* Contest Grid */
  .luqta-contests-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 20px;
  }

  /* Contest Card - Figma Design */
  .luqta-contest-card {
    background: var(--luqta-bg);
    border-radius: 16px;
    overflow: hidden;
    transition: all 0.2s ease;
    cursor: pointer;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    position: relative;
    border: 1px solid var(--luqta-border);
  }

  .luqta-contest-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }

  .luqta-contest-card.completed {
    opacity: 0.9;
  }

  /* Banner with carousel support */
  .luqta-contest-banner {
    position: relative;
    height: 180px;
    background: linear-gradient(135deg, var(--luqta-bg-medium) 0%, var(--luqta-border) 100%);
    overflow: hidden;
  }

  .luqta-contest-banner-carousel {
    width: 100%;
    height: 100%;
    position: relative;
  }

  .luqta-contest-banner-slide {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    transition: opacity 0.5s ease;
  }

  .luqta-contest-banner-slide.active {
    opacity: 1;
  }

  .luqta-contest-banner-slide img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .luqta-contest-banner > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  /* Multiple company logos container - Avatar Stack */
  .luqta-contest-logos {
    position: absolute;
    top: 10px;
    left: 10px;
    display: flex;
    align-items: center;
    z-index: 4;
  }

  /* Logo - Circle Avatar Style */
  .luqta-contest-logo {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: var(--luqta-bg);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
    overflow: hidden;
    border: 2px solid var(--luqta-bg);
    flex-shrink: 0;
  }

  .luqta-contest-logo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }

  /* Badges container at bottom of banner */
  .luqta-contest-badges {
    position: absolute;
    bottom: 10px;
    left: 10px;
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    z-index: 3;
  }

  .luqta-contest-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 5px 10px;
    border-radius: 16px;
    font-size: 11px;
    font-weight: 500;
    background: var(--luqta-image-badge-bg);
    color: var(--luqta-image-badge-text);
    backdrop-filter: blur(8px);
  }

  .luqta-contest-badge svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
    color: var(--luqta-image-badge-text);
  }

  .luqta-contest-badge.timer {
    color: var(--luqta-image-badge-text);
  }

  .luqta-contest-badge.timer svg {
    color: var(--luqta-warning);
  }

  .luqta-contest-badge.levels {
    color: var(--luqta-image-badge-text);
  }

  .luqta-contest-badge.levels svg {
    color: var(--luqta-image-badge-text);
    opacity: 0.7;
  }

  .luqta-contest-badge.completed-badge {
    background: var(--luqta-success);
    color: white;
  }

  /* Carousel dots - top right of banner */
  .luqta-carousel-dots {
    position: absolute;
    top: 10px;
    right: 10px;
    display: flex;
    gap: 4px;
    z-index: 4;
  }

  .luqta-carousel-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--luqta-image-badge-text);
    opacity: 0.5;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .luqta-carousel-dot.active {
    background: var(--luqta-image-badge-text);
    opacity: 0.95;
    width: 14px;
    border-radius: 3px;
  }

  /* Card body */
  .luqta-contest-body {
    padding: 12px;
    background: var(--luqta-bg);
  }

  .luqta-contest-title {
    font-size: 14px;
    font-weight: 700;
    margin: 0 0 4px;
    line-height: 1.3;
    color: var(--luqta-text);
  }

  .luqta-contest-desc {
    font-size: 11px;
    color: var(--luqta-text-muted);
    margin: 0;
    line-height: 1.5;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .luqta-contest-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 8px;
    position: relative;
    z-index: 10;
  }

  .luqta-contest-prize {
    display: none;
  }

  .luqta-contest-join-btn {
    padding: 8px 20px;
    border: none;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: inherit;
    background: var(--luqta-primary);
    color: white;
  }

  .luqta-contest-join-btn:hover {
    transform: scale(1.02);
    box-shadow: 0 4px 12px var(--luqta-primary-shadow);
  }

  .luqta-contest-join-btn.participating {
    background: var(--luqta-primary-light);
    color: var(--luqta-primary);
  }

  .luqta-contest-join-btn.completed {
    background: var(--luqta-success);
    color: white;
  }

  /* Old meta styles kept for compatibility */
  .luqta-contest-meta {
    display: flex;
    gap: 16px;
    font-size: 12px;
    color: var(--luqta-text-muted);
  }

  .luqta-contest-meta-item {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .luqta-contest-progress {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid var(--luqta-border);
  }

  .luqta-progress-bar {
    height: 6px;
    background: var(--luqta-border);
    border-radius: 3px;
    overflow: hidden;
    margin-bottom: 8px;
  }

  .luqta-progress-fill {
    height: 100%;
    background: linear-gradient(90deg, var(--luqta-primary), var(--luqta-secondary));
    border-radius: 3px;
    transition: width 0.3s ease;
  }

  .luqta-progress-text {
    font-size: 12px;
    color: var(--luqta-text-muted);
    display: flex;
    justify-content: space-between;
  }

  /* Type badges - keep for backwards compatibility */
  .luqta-contest-type-badge {
    position: absolute;
    top: 12px;
    right: 12px;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    background: var(--luqta-badge-bg);
    color: var(--luqta-text);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .luqta-contest-type-badge.public {
    background: var(--luqta-success);
    color: white;
  }

  .luqta-contest-type-badge.private {
    background: var(--luqta-warning);
    color: white;
  }

  .luqta-contest-type-badge.vip {
    background: linear-gradient(135deg, var(--luqta-warning), var(--luqta-warning-dark));
    color: white;
  }

  /* Buttons */
  .luqta-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 24px;
    border: none;
    border-radius: var(--luqta-radius);
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: inherit;
  }

  .luqta-btn-primary {
    background: linear-gradient(135deg, var(--luqta-primary), var(--luqta-secondary));
    color: white;
  }

  .luqta-btn-primary:hover {
    transform: translateY(-1px);
    box-shadow: var(--luqta-shadow);
  }

  .luqta-btn-primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  .luqta-btn-secondary {
    background: var(--luqta-primary-light);
    color: var(--luqta-primary);
  }

  .luqta-btn-secondary:hover {
    background: var(--luqta-primary-medium);
  }

  .luqta-btn-outline {
    background: transparent;
    border: 2px solid var(--luqta-border);
    color: var(--luqta-text);
  }

  .luqta-btn-outline:hover {
    border-color: var(--luqta-primary);
    color: var(--luqta-primary);
  }

  .luqta-btn-block {
    width: 100%;
  }

  .luqta-btn-sm {
    padding: 8px 16px;
    font-size: 13px;
  }

  /* Modal / Popup */
  .luqta-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--luqta-overlay);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
    animation: luqta-fade-in 0.2s ease;
  }

  @keyframes luqta-fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  .luqta-modal {
    background: var(--luqta-bg);
    border-radius: var(--luqta-radius-lg);
    max-width: 480px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    animation: luqta-slide-up 0.3s ease;
  }

  @keyframes luqta-slide-up {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .luqta-modal-header {
    padding: 20px;
    border-bottom: 1px solid var(--luqta-border);
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .luqta-modal-title {
    font-size: 18px;
    font-weight: 600;
    margin: 0;
  }

  .luqta-modal-close {
    width: 32px;
    height: 32px;
    border: none;
    background: var(--luqta-border);
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    color: var(--luqta-text-muted);
    transition: all 0.2s ease;
  }

  .luqta-modal-close:hover {
    background: var(--luqta-primary-light);
    color: var(--luqta-primary);
  }

  .luqta-modal-body {
    padding: 20px;
  }

  .luqta-modal-footer {
    padding: 20px;
    border-top: 1px solid var(--luqta-border);
    display: flex;
    gap: 12px;
    justify-content: flex-end;
  }

  /* Contest Detail View */
  .luqta-contest-detail {
    padding: 0;
  }

  .luqta-contest-detail-banner {
    height: 200px;
    background: linear-gradient(135deg, var(--luqta-primary), var(--luqta-secondary));
    position: relative;
  }

  .luqta-contest-detail-banner img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .luqta-back-btn {
    position: absolute;
    top: 16px;
    left: 16px;
    width: 40px;
    height: 40px;
    border: none;
    background: var(--luqta-image-badge-bg);
    backdrop-filter: blur(8px);
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    transition: all 0.2s ease;
  }

  .luqta-back-btn:hover {
    background: var(--luqta-overlay);
    transform: scale(1.05);
  }

  .luqta-back-btn svg {
    width: 20px;
    height: 20px;
    color: var(--luqta-image-badge-text);
  }

  .luqta-contest-detail-content {
    padding: 20px;
  }

  .luqta-contest-detail-title {
    font-size: 24px;
    font-weight: 700;
    margin: 0 0 12px;
  }

  .luqta-contest-detail-desc {
    font-size: 14px;
    color: var(--luqta-text-muted);
    line-height: 1.6;
    margin: 0 0 20px;
  }

  .luqta-contest-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin-bottom: 24px;
  }

  .luqta-stat-card {
    background: var(--luqta-primary-light);
    padding: 16px;
    border-radius: var(--luqta-radius);
    text-align: center;
  }

  .luqta-stat-value {
    font-size: 24px;
    font-weight: 700;
    color: var(--luqta-primary);
    margin-bottom: 4px;
  }

  .luqta-stat-label {
    font-size: 12px;
    color: var(--luqta-text-muted);
  }

  /* Levels List */
  .luqta-levels-section {
    margin-top: 24px;
  }

  .luqta-levels-title {
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 16px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .luqta-levels-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .luqta-level-card {
    background: var(--luqta-bg);
    border: 1px solid var(--luqta-border);
    border-radius: var(--luqta-radius);
    padding: 16px;
    display: flex;
    align-items: center;
    gap: 16px;
    transition: all 0.2s ease;
    cursor: pointer;
  }

  .luqta-level-card:hover:not(.completed):not(.locked) {
    border-color: var(--luqta-primary);
    box-shadow: var(--luqta-shadow);
  }

  .luqta-level-card.completed {
    background: var(--luqta-primary-light);
    border-color: var(--luqta-primary);
    cursor: default;
  }

  .luqta-level-card.locked {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .luqta-level-icon {
    width: 48px;
    height: 48px;
    border-radius: var(--luqta-radius-sm);
    background: var(--luqta-border);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    flex-shrink: 0;
  }

  .luqta-level-card.completed .luqta-level-icon {
    background: var(--luqta-success);
    color: white;
  }

  .luqta-level-info {
    flex: 1;
    min-width: 0;
  }

  .luqta-level-name {
    font-size: 15px;
    font-weight: 600;
    margin: 0 0 4px;
  }

  .luqta-level-desc {
    font-size: 13px;
    color: var(--luqta-text-muted);
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .luqta-level-points {
    background: var(--luqta-primary-light);
    color: var(--luqta-primary);
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 600;
    flex-shrink: 0;
  }

  .luqta-level-card.completed .luqta-level-points {
    background: var(--luqta-success);
    color: white;
  }

  /* Level Type Icons */
  .luqta-level-type {
    font-size: 12px;
    color: var(--luqta-text-muted);
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 4px;
  }

  /* Input Fields */
  .luqta-input-group {
    margin-bottom: 16px;
  }

  .luqta-input-label {
    display: block;
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 8px;
  }

  .luqta-input {
    width: 100%;
    padding: 12px 16px;
    border: 1px solid var(--luqta-border);
    border-radius: var(--luqta-radius);
    font-size: 14px;
    font-family: inherit;
    background: var(--luqta-bg);
    color: var(--luqta-text);
    transition: all 0.2s ease;
  }

  .luqta-input:focus {
    outline: none;
    border-color: var(--luqta-primary);
    box-shadow: 0 0 0 3px var(--luqta-primary-light);
    background: var(--luqta-bg);
  }

  .luqta-input::placeholder {
    color: var(--luqta-text-muted);
  }

  /* Congratulation Popup */
  .luqta-congrats {
    text-align: center;
    padding: 40px 20px;
  }

  .luqta-congrats-icon {
    width: 80px;
    height: 80px;
    background: linear-gradient(135deg, var(--luqta-success), var(--luqta-success-dark));
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    font-size: 40px;
    color: white;
    animation: luqta-bounce 0.5s ease;
  }

  @keyframes luqta-bounce {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.1); }
  }

  .luqta-congrats-title {
    font-size: 24px;
    font-weight: 700;
    margin: 0 0 8px;
    color: var(--luqta-success);
  }

  .luqta-congrats-message {
    font-size: 14px;
    color: var(--luqta-text-muted);
    margin: 0 0 24px;
  }

  .luqta-congrats-points {
    background: var(--luqta-primary-light);
    padding: 16px 24px;
    border-radius: var(--luqta-radius);
    display: inline-block;
  }

  .luqta-congrats-points-value {
    font-size: 32px;
    font-weight: 700;
    color: var(--luqta-primary);
  }

  .luqta-congrats-points-label {
    font-size: 14px;
    color: var(--luqta-text-muted);
  }

  /* Participate Popup */
  .luqta-participate {
    text-align: center;
  }

  .luqta-participate-icon {
    width: 64px;
    height: 64px;
    background: var(--luqta-primary-light);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
    font-size: 28px;
    color: var(--luqta-primary);
  }

  .luqta-participate-title {
    font-size: 20px;
    font-weight: 600;
    margin: 0 0 8px;
  }

  .luqta-participate-desc {
    font-size: 14px;
    color: var(--luqta-text-muted);
    margin: 0 0 24px;
  }

  /* Empty State */
  .luqta-empty {
    text-align: center;
    padding: 60px 20px;
  }

  .luqta-empty-icon {
    font-size: 48px;
    margin-bottom: 16px;
    opacity: 0.5;
  }

  .luqta-empty-title {
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 8px;
  }

  .luqta-empty-desc {
    font-size: 14px;
    color: var(--luqta-text-muted);
    margin: 0;
  }

  /* Toast Notifications */
  .luqta-toast-container {
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 1100;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .luqta-toast {
    background: var(--luqta-bg);
    padding: 16px 20px;
    border-radius: var(--luqta-radius);
    box-shadow: var(--luqta-shadow-lg);
    display: flex;
    align-items: center;
    gap: 12px;
    animation: luqta-slide-in 0.3s ease;
    max-width: 360px;
  }

  @keyframes luqta-slide-in {
    from {
      opacity: 0;
      transform: translateX(100%);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  .luqta-toast.success {
    border-left: 4px solid var(--luqta-success);
  }

  .luqta-toast.error {
    border-left: 4px solid var(--luqta-error);
  }

  .luqta-toast-icon {
    font-size: 20px;
  }

  .luqta-toast.success .luqta-toast-icon {
    color: var(--luqta-success);
  }

  .luqta-toast.error .luqta-toast-icon {
    color: var(--luqta-error);
  }

  .luqta-toast-message {
    font-size: 14px;
    flex: 1;
  }

  /* Level Status Badge */
  .luqta-level-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 4px;
  }

  .luqta-level-status {
    font-size: 11px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 10px;
  }

  .luqta-level-status.completed {
    background: var(--luqta-success);
    color: white;
  }

  .luqta-level-status.in-progress {
    background: var(--luqta-warning);
    color: white;
  }

  .luqta-level-card.in-progress {
    border-color: var(--luqta-warning);
    background: var(--luqta-warning-lighter);
  }

  /* Quiz Modal */
  .luqta-quiz-modal {
    max-width: 480px;
    background: #fff;
    border-radius: 20px;
    padding: 0;
    overflow: hidden;
  }

  .luqta-quiz-body {
    padding: 28px 24px;
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .luqta-quiz-timer {
    display: flex;
    align-items: center;
    gap: 4px;
    color: var(--luqta-warning);
    font-weight: 600;
    margin-bottom: 12px;
  }

  .luqta-quiz-instruction {
    font-size: 13px;
    color: #E85D3A;
    margin-bottom: 16px;
    line-height: 1.4;
  }

  .luqta-quiz-question {
    padding: 0;
  }

  .luqta-question-text {
    font-size: 20px;
    font-weight: 700;
    margin: 0 0 24px 0;
    line-height: 1.4;
    color: #1a1a2e;
  }

  .luqta-quiz-options {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .luqta-quiz-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 16px;
    background: #fff;
    border: 2px solid #e0e0e0;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    text-align: left;
    font-family: inherit;
    font-size: 15px;
    color: #1a1a2e;
  }

  .luqta-quiz-option:hover:not(.selected):not(:disabled) {
    border-color: #bbb;
  }

  .luqta-quiz-option.selected {
    border-color: var(--luqta-primary);
    background: #faf8ff;
  }

  .luqta-quiz-option:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }

  .luqta-option-text {
    flex: 1;
  }

  .luqta-option-radio {
    width: 24px;
    height: 24px;
    min-width: 24px;
    border: 2px solid #ccc;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    background: #fff;
  }

  .luqta-option-radio.checked {
    border-color: var(--luqta-primary);
    background: var(--luqta-primary);
  }

  .luqta-option-radio.checked svg {
    display: block;
  }

  .luqta-quiz-submit-btn {
    margin-top: 24px;
    width: 100%;
    padding: 14px;
    border: none;
    border-radius: 999px;
    font-size: 16px;
    font-weight: 600;
    font-family: inherit;
    cursor: not-allowed;
    background: #d5d5d5;
    color: #fff;
    transition: all 0.2s ease;
  }

  .luqta-quiz-submit-btn.enabled {
    cursor: pointer;
    background: linear-gradient(135deg, var(--luqta-primary), #8b5cf6);
    color: #fff;
  }

  .luqta-quiz-submit-btn.enabled:hover {
    opacity: 0.9;
  }

  .luqta-quiz-feedback {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    border-radius: 12px;
    margin-top: 16px;
    font-weight: 600;
    font-size: 14px;
  }

  .luqta-quiz-feedback.correct {
    background: var(--luqta-success-light);
    color: var(--luqta-success);
  }

  .luqta-quiz-feedback.incorrect {
    background: var(--luqta-error-light);
    color: var(--luqta-error);
  }

  /* QR Scanner Styles */
  .luqta-qr-scanner-container {
    text-align: center;
  }

  .luqta-qr-scanner-preview {
    position: relative;
    width: 100%;
    max-width: 300px;
    height: 250px;
    margin: 0 auto 16px;
    background: var(--luqta-overlay);
    border-radius: var(--luqta-radius);
    overflow: hidden;
    display: none;
  }

  .luqta-qr-scanner-preview.active {
    display: block;
  }

  .luqta-qr-scanner-preview video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .luqta-qr-scanner-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }

  .luqta-qr-scanner-frame {
    width: 180px;
    height: 180px;
    border: 3px solid var(--luqta-inverted-border-strong);
    border-radius: 12px;
    position: relative;
    box-shadow: 0 0 0 9999px var(--luqta-overlay);
  }

  .luqta-qr-scanner-frame::before,
  .luqta-qr-scanner-frame::after {
    content: '';
    position: absolute;
    width: 30px;
    height: 30px;
    border-color: var(--luqta-primary);
    border-style: solid;
  }

  .luqta-qr-scanner-frame::before {
    top: -3px;
    left: -3px;
    border-width: 4px 0 0 4px;
    border-radius: 12px 0 0 0;
  }

  .luqta-qr-scanner-frame::after {
    bottom: -3px;
    right: -3px;
    border-width: 0 4px 4px 0;
    border-radius: 0 0 12px 0;
  }

  .luqta-qr-instruction {
    font-size: 14px;
    color: var(--luqta-text-muted);
    margin: 0 0 16px;
  }

  .luqta-qr-status {
    margin-top: 12px;
    padding: 12px;
    border-radius: var(--luqta-radius);
    font-size: 13px;
  }

  .luqta-qr-status.scanning {
    background: var(--luqta-primary-light);
    color: var(--luqta-primary);
  }

  .luqta-qr-status.success {
    background: var(--luqta-success-light);
    color: var(--luqta-success);
  }

  .luqta-qr-status.error {
    background: var(--luqta-error-light);
    color: var(--luqta-error);
  }

  /* Quiz Result Modal */
  .luqta-quiz-result {
    text-align: center;
    padding: 20px;
  }

  .luqta-result-icon {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    font-size: 40px;
    animation: luqta-bounce 0.5s ease;
  }

  .luqta-result-icon.passed {
    background: linear-gradient(135deg, var(--luqta-success), var(--luqta-success-dark));
    color: white;
  }

  .luqta-result-icon.failed {
    background: linear-gradient(135deg, var(--luqta-error), var(--luqta-error-dark));
    color: white;
  }

  .luqta-result-title {
    font-size: 24px;
    font-weight: 700;
    margin: 0 0 12px;
  }

  .luqta-result-grade {
    font-size: 48px;
    font-weight: 800;
    margin-bottom: 8px;
  }

  .luqta-result-grade.passed {
    color: var(--luqta-success);
  }

  .luqta-result-grade.failed {
    color: var(--luqta-error);
  }

  .luqta-result-remarks {
    font-size: 14px;
    color: var(--luqta-text-muted);
    margin: 0 0 24px;
  }

  .luqta-result-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin-bottom: 24px;
  }

  .luqta-result-stat {
    background: var(--luqta-primary-light);
    padding: 16px 12px;
    border-radius: var(--luqta-radius);
  }

  .luqta-result-stat-value {
    font-size: 20px;
    font-weight: 700;
    color: var(--luqta-primary);
    margin-bottom: 4px;
  }

  .luqta-result-stat-label {
    font-size: 11px;
    color: var(--luqta-text-muted);
  }

  .luqta-result-status {
    display: inline-block;
    padding: 8px 24px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 600;
  }

  .luqta-result-status.passed {
    background: var(--luqta-success-light);
    color: var(--luqta-success);
  }

  .luqta-result-status.failed {
    background: var(--luqta-error-light);
    color: var(--luqta-error);
  }

  /* Participation Modal Styles */
  .luqta-participation-content {
    text-align: center;
    padding: 20px 0;
  }

  .luqta-participation-icon {
    width: 64px;
    height: 64px;
    margin: 0 auto 16px;
    background: var(--luqta-primary-light);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .luqta-participation-icon svg {
    width: 32px;
    height: 32px;
    color: var(--luqta-primary);
  }

  .luqta-participation-content h3 {
    font-size: 18px;
    font-weight: 700;
    margin: 0 0 8px;
    color: var(--luqta-text);
  }

  .luqta-participation-content p {
    font-size: 14px;
    color: var(--luqta-text-muted);
    margin: 0 0 16px;
  }

  .luqta-access-code-section {
    margin-top: 16px;
    text-align: left;
  }

  .luqta-private-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: var(--luqta-warning-light);
    color: var(--luqta-warning);
    border-radius: 16px;
    font-size: 12px;
    font-weight: 500;
    margin-bottom: 12px;
  }

  .luqta-private-badge svg {
    width: 14px;
    height: 14px;
  }

  .luqta-input {
    width: 100%;
    padding: 12px 16px;
    border: 1px solid var(--luqta-border);
    border-radius: var(--luqta-radius);
    font-size: 14px;
    font-family: inherit;
    background: var(--luqta-bg);
    color: var(--luqta-text);
    transition: border-color 0.2s ease;
  }

  .luqta-input:focus {
    outline: none;
    border-color: var(--luqta-primary);
    background: var(--luqta-bg);
  }

  .luqta-input::placeholder {
    color: var(--luqta-text-light);
  }

  /* Loading Spinner */
  .luqta-loading-spinner {
    text-align: center;
    color: var(--luqta-text);
  }

  .luqta-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid var(--luqta-inverted-border);
    border-top-color: var(--luqta-primary);
    border-radius: 50%;
    animation: luqta-spin 1s linear infinite;
    margin: 0 auto 12px;
  }

  @keyframes luqta-spin {
    to {
      transform: rotate(360deg);
    }
  }

  .luqta-loading-spinner p {
    margin: 0;
    font-size: 14px;
  }

  /* ============================================================================
     CONTEST DETAIL V2 (Figma Design)
     ============================================================================ */

  .luqta-contest-detail-v2 {
    background: var(--luqta-bg-light);
    min-height: 100%;
  }

  /* Detail Banner */
  .luqta-detail-banner {
    position: relative;
    height: 280px;
    background: linear-gradient(135deg, var(--luqta-primary), var(--luqta-secondary));
    overflow: hidden;
  }

  .luqta-detail-banner-carousel {
    width: 100%;
    height: 100%;
    position: relative;
  }

  .luqta-detail-banner-slide {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    transition: opacity 0.5s ease;
  }

  .luqta-detail-banner-slide.active {
    opacity: 1;
  }

  .luqta-detail-banner-slide img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .luqta-detail-carousel-dots {
    position: absolute;
    bottom: 16px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 6px;
    z-index: 5;
  }

  .luqta-detail-carousel-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--luqta-image-badge-text);
    opacity: 0.5;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .luqta-detail-carousel-dot.active {
    background: var(--luqta-image-badge-text);
    opacity: 0.95;
    width: 20px;
    border-radius: 4px;
  }

  .luqta-share-btn {
    position: absolute;
    top: 16px;
    right: 16px;
    width: 40px;
    height: 40px;
    border: none;
    background: var(--luqta-image-badge-bg);
    backdrop-filter: blur(8px);
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    z-index: 5;
  }

  .luqta-share-btn:hover {
    background: var(--luqta-overlay);
    transform: scale(1.05);
  }

  .luqta-share-btn svg {
    width: 20px;
    height: 20px;
    color: var(--luqta-image-badge-text);
  }

  /* Detail Content */
  .luqta-detail-content {
    padding: 20px;
    background: var(--luqta-bg-light);
  }

  .luqta-detail-header {
    background: var(--luqta-bg);
    border-radius: 16px;
    padding: 20px;
    margin-bottom: 16px;
  }

  .luqta-detail-title {
    font-size: 20px;
    font-weight: 700;
    margin: 0 0 8px;
    color: var(--luqta-text);
  }

  .luqta-detail-desc {
    font-size: 14px;
    color: var(--luqta-text-muted);
    line-height: 1.6;
    margin: 0;
  }

  .luqta-see-more {
    color: var(--luqta-primary);
    font-weight: 600;
    text-decoration: none;
  }

  /* Detail Section Title */
  .luqta-detail-section-title {
    font-size: 16px;
    font-weight: 700;
    margin: 0 0 16px;
    color: var(--luqta-text);
  }

  /* Progress Section */
  .luqta-detail-progress-section {
    background: var(--luqta-bg);
    border-radius: 16px;
    padding: 20px;
    margin-bottom: 16px;
  }

  .luqta-detail-progress-badges {
    display: flex;
    gap: 8px;
    margin-bottom: 12px;
  }

  .luqta-detail-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 500;
    background: var(--luqta-bg-medium);
    color: var(--luqta-text);
  }

  .luqta-detail-badge svg {
    width: 16px;
    height: 16px;
  }

  .luqta-detail-badge.timer svg {
    color: var(--luqta-warning);
  }

  .luqta-detail-badge.levels svg {
    color: var(--luqta-text-muted);
  }

  .luqta-detail-progress-hint {
    font-size: 13px;
    color: var(--luqta-text-muted);
    margin: 0 0 16px;
  }

  .luqta-detail-progress-bar {
    height: 12px;
    background: var(--luqta-border);
    border-radius: 6px;
    overflow: hidden;
    position: relative;
  }

  .luqta-detail-progress-fill {
    height: 100%;
    background: linear-gradient(90deg, var(--luqta-primary), var(--luqta-secondary));
    border-radius: 6px;
    transition: width 0.5s ease;
  }

  .luqta-progress-check {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    color: white;
  }

  .luqta-progress-check svg {
    width: 16px;
    height: 16px;
  }

  /* Levels Section */
  .luqta-detail-levels-section {
    background: var(--luqta-bg);
    border-radius: 16px;
    padding: 20px;
    margin-bottom: 16px;
  }

  .luqta-detail-levels-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .luqta-detail-level-card {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    background: var(--luqta-bg-light);
    border-radius: 12px;
    border: 1px solid var(--luqta-border);
    transition: all 0.2s ease;
  }

  .luqta-detail-level-card.completed {
    background: var(--luqta-success-lighter);
    border-color: var(--luqta-success-light);
  }

  .luqta-detail-level-card.locked {
    opacity: 0.6;
  }

  .luqta-detail-level-icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: var(--luqta-primary-light);
  }

  .luqta-detail-level-icon svg {
    width: 24px;
    height: 24px;
    color: var(--luqta-primary);
  }

  .luqta-detail-level-icon.text {
    background: var(--luqta-primary-light);
  }
  .luqta-detail-level-icon.text svg {
    color: var(--luqta-secondary);
  }

  .luqta-detail-level-icon.qr {
    background: var(--luqta-success-light);
  }
  .luqta-detail-level-icon.qr svg {
    color: var(--luqta-success);
  }

  .luqta-detail-level-icon.link {
    background: var(--luqta-primary-light);
  }
  .luqta-detail-level-icon.link svg {
    color: var(--luqta-info);
  }

  .luqta-detail-level-icon.image {
    background: var(--luqta-warning-light);
  }
  .luqta-detail-level-icon.image svg {
    color: var(--luqta-warning);
  }

  .luqta-detail-level-icon.quiz {
    background: var(--luqta-primary-light);
  }
  .luqta-detail-level-icon.quiz svg {
    color: var(--luqta-primary);
  }

  .luqta-detail-level-card.completed .luqta-detail-level-icon {
    background: var(--luqta-success-light);
  }
  .luqta-detail-level-card.completed .luqta-detail-level-icon svg {
    color: var(--luqta-success);
  }

  .luqta-detail-level-info {
    flex: 1;
    min-width: 0;
  }

  .luqta-detail-level-name {
    font-size: 15px;
    font-weight: 600;
    margin: 0 0 4px;
    color: var(--luqta-text);
  }

  .luqta-detail-level-status {
    font-size: 12px;
    color: var(--luqta-text-muted);
  }

  .luqta-detail-level-status.completed {
    color: var(--luqta-success);
    font-weight: 500;
  }

  .luqta-detail-level-action {
    flex-shrink: 0;
  }

  .luqta-detail-level-play {
    padding: 10px 24px;
    border: none;
    border-radius: 20px;
    background: var(--luqta-primary);
    color: white;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: inherit;
  }

  .luqta-detail-level-play:hover {
    transform: scale(1.02);
    box-shadow: 0 4px 12px var(--luqta-primary-shadow);
  }

  .luqta-detail-level-check {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: var(--luqta-success);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .luqta-detail-level-check svg {
    width: 18px;
    height: 18px;
    color: white;
  }

  .luqta-detail-level-locked {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: var(--luqta-border);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .luqta-detail-level-locked svg {
    width: 16px;
    height: 16px;
    color: var(--luqta-text-light);
  }

  /* Companies Section */
  .luqta-detail-companies-section {
    margin-bottom: 16px;
  }

  .luqta-detail-companies-section .luqta-detail-section-title {
    padding: 0 4px;
  }

  .luqta-detail-companies-scroll {
    display: flex;
    gap: 12px;
    overflow-x: auto;
    padding-bottom: 8px;
    -webkit-overflow-scrolling: touch;
  }

  .luqta-detail-companies-scroll::-webkit-scrollbar {
    display: none;
  }

  .luqta-detail-company-card {
    flex-shrink: 0;
    width: 160px;
    background: var(--luqta-bg);
    border-radius: 12px;
    padding: 16px;
    text-align: center;
  }

  .luqta-detail-company-logo {
    width: 64px;
    height: 64px;
    border-radius: 12px;
    margin: 0 auto 12px;
    overflow: hidden;
    background: var(--luqta-bg-medium);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .luqta-detail-company-logo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .luqta-detail-company-logo.placeholder svg {
    width: 32px;
    height: 32px;
    color: var(--luqta-text-light);
  }

  .luqta-detail-company-name {
    font-size: 13px;
    font-weight: 600;
    margin: 0 0 4px;
    color: var(--luqta-text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .luqta-detail-company-desc {
    font-size: 11px;
    color: var(--luqta-text-muted);
    margin: 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  /* Prizes Section */
  .luqta-detail-prizes-section {
    margin-bottom: 16px;
  }

  .luqta-detail-prizes-section .luqta-detail-section-title {
    padding: 0 4px;
  }

  .luqta-detail-prizes-list {
    display: flex;
    gap: 12px;
    overflow-x: auto;
    padding-bottom: 8px;
    -webkit-overflow-scrolling: touch;
  }

  .luqta-detail-prizes-list::-webkit-scrollbar {
    display: none;
  }

  .luqta-detail-prize-card {
    flex-shrink: 0;
    width: 140px;
    background: var(--luqta-bg);
    border-radius: 12px;
    overflow: hidden;
  }

  .luqta-detail-prize-image {
    width: 100%;
    height: 100px;
    background: linear-gradient(135deg, var(--luqta-warning-light), var(--luqta-warning-light));
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .luqta-detail-prize-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .luqta-detail-prize-image.placeholder svg {
    width: 40px;
    height: 40px;
    color: var(--luqta-warning);
  }

  .luqta-detail-prize-info {
    padding: 12px;
  }

  .luqta-detail-prize-name {
    font-size: 13px;
    font-weight: 600;
    margin: 0 0 4px;
    color: var(--luqta-text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .luqta-detail-prize-desc {
    font-size: 11px;
    color: var(--luqta-text-muted);
    margin: 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  /* ============================================================================
     CONGRATULATION MODAL V2 (Figma Design - Exact Match)
     ============================================================================ */

  .luqta-congrats-modal-v2 {
    max-width: 380px;
    border-radius: 24px;
    overflow: visible;
    background: var(--luqta-bg);
  }

  .luqta-congrats-modal-v2 .luqta-modal-body {
    padding: 0;
  }

  .luqta-congrats-content {
    text-align: center;
    padding: 50px 32px 32px;
    position: relative;
    overflow: visible;
  }

  /* Confetti Decorations */
  .luqta-confetti-wrap {
    position: absolute;
    top: 80px;
    left: 50%;
    transform: translateX(-50%);
    width: 200px;
    height: 150px;
    pointer-events: none;
  }

  .luqta-confetti-piece {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 8px;
    height: 16px;
    background: var(--c);
    border-radius: 2px;
    transform: translate(var(--x), var(--y)) rotate(var(--r));
    animation: luqta-confetti-float 3s ease-in-out infinite;
  }

  .luqta-confetti-spiral {
    width: 12px;
    height: 12px;
    border: 3px solid var(--c);
    border-radius: 50%;
    background: transparent;
    border-top-color: transparent;
    border-left-color: transparent;
  }

  .luqta-confetti-dot {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 8px;
    height: 8px;
    background: var(--c);
    border-radius: 50%;
    transform: translate(var(--x), var(--y));
    animation: luqta-confetti-pulse 2s ease-in-out infinite;
  }

  @keyframes luqta-confetti-float {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; transform: translate(var(--x), calc(var(--y) - 5px)) rotate(var(--r)); }
  }

  @keyframes luqta-confetti-pulse {
    0%, 100% { transform: translate(var(--x), var(--y)) scale(1); }
    50% { transform: translate(var(--x), var(--y)) scale(1.2); }
  }

  /* Medal Badge - Purple with Star (Figma Exact) */
  .luqta-medal-container {
    position: relative;
    margin: 0 auto 24px;
    width: 120px;
    height: 140px;
  }

  .luqta-medal-badge-v2 {
    position: relative;
    width: 100%;
    height: 100%;
  }

  .luqta-medal-outer {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 100px;
    background: linear-gradient(135deg, var(--luqta-primary) 0%, var(--luqta-secondary) 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 8px 24px var(--luqta-primary-medium);
    animation: luqta-medal-pop 0.6s ease;
  }

  @keyframes luqta-medal-pop {
    0% { transform: translateX(-50%) scale(0); }
    50% { transform: translateX(-50%) scale(1.1); }
    100% { transform: translateX(-50%) scale(1); }
  }

  .luqta-medal-inner {
    width: 70px;
    height: 70px;
    background: linear-gradient(135deg, var(--luqta-secondary) 0%, var(--luqta-primary) 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 3px solid var(--luqta-inverted-border);
  }

  .luqta-medal-star {
    width: 36px;
    height: 36px;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
  }

  .luqta-medal-ribbon-left,
  .luqta-medal-ribbon-right {
    position: absolute;
    bottom: 0;
    width: 24px;
    height: 50px;
    background: linear-gradient(180deg, var(--luqta-secondary) 0%, var(--luqta-primary) 100%);
  }

  .luqta-medal-ribbon-left {
    left: 28px;
    transform: skewX(15deg);
    border-radius: 0 0 4px 4px;
  }

  .luqta-medal-ribbon-right {
    right: 28px;
    transform: skewX(-15deg);
    border-radius: 0 0 4px 4px;
  }

  /* Badge Container (SVG Badge Icon) */
  .luqta-badge-container {
    margin: 0 auto 16px;
    width: 120px;
    height: 140px;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: luqta-badge-bounce 0.6s ease;
  }

  .luqta-badge-container svg {
    width: 100%;
    height: 100%;
    filter: drop-shadow(0 8px 24px var(--luqta-primary-shadow));
  }

  @keyframes luqta-badge-bounce {
    0% { transform: scale(0); }
    50% { transform: scale(1.1); }
    100% { transform: scale(1); }
  }

  /* Title */
  .luqta-congrats-title-v2 {
    font-size: 24px;
    font-weight: 700;
    margin: 0 0 8px;
    color: var(--luqta-text);
  }

  /* Level Name */
  .luqta-congrats-level-name {
    font-size: 14px;
    color: var(--luqta-text-muted);
    margin: 0 0 8px;
    font-weight: 500;
  }

  /* Points Text */
  .luqta-congrats-points-text-v2 {
    font-size: 16px;
    color: var(--luqta-text-muted);
    margin: 0 0 20px;
  }

  .luqta-points-value {
    color: var(--luqta-warning);
    font-weight: 700;
  }

  /* Brand Logo */
  .luqta-congrats-brand {
    margin: 16px auto;
    width: 60px;
    height: 24px;
  }

  .luqta-congrats-brand img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .luqta-brand-text {
    font-size: 12px;
    color: var(--luqta-text-light);
    font-weight: 500;
  }

  /* Time Box */
  .luqta-time-box {
    background: var(--luqta-bg-light);
    border-radius: 16px;
    padding: 16px 24px;
    margin: 16px auto 24px;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    min-width: 180px;
  }

  .luqta-time-icon-wrap {
    color: var(--luqta-text-light);
    margin-bottom: 4px;
  }

  .luqta-time-label-text {
    font-size: 12px;
    color: var(--luqta-text-light);
  }

  .luqta-time-value-display {
    font-size: 32px;
    font-weight: 700;
    color: var(--luqta-text);
    font-variant-numeric: tabular-nums;
    letter-spacing: 2px;
  }

  /* Action Buttons */
  .luqta-congrats-btns {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 8px;
  }

  .luqta-share-btn-v2 {
    width: 100%;
    padding: 14px 24px;
    border: 1px solid var(--luqta-border);
    border-radius: 12px;
    background: var(--luqta-bg);
    color: var(--luqta-text);
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: inherit;
  }

  .luqta-share-btn-v2:hover {
    background: var(--luqta-bg-light);
    border-color: var(--luqta-border-medium);
  }

  .luqta-next-btn-v2 {
    width: 100%;
    padding: 16px 24px;
    border: none;
    border-radius: 12px;
    background: linear-gradient(135deg, var(--luqta-success) 0%, var(--luqta-success-dark) 100%);
    color: white;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: inherit;
  }

  .luqta-next-btn-v2:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px var(--luqta-success-shadow);
  }

  /* ============================================================================
     LEVEL MODALS (QR & Image - Figma Design)
     ============================================================================ */

  .luqta-level-modal-special {
    max-width: 380px;
    width: 100%;
    border-radius: 24px;
    position: relative;
    background: var(--luqta-bg);
    overflow: hidden;
  }

  .luqta-modal-close-float {
    position: absolute;
    top: 16px;
    right: 16px;
    width: 36px;
    height: 36px;
    border: none;
    background: var(--luqta-bg-medium);
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    transition: all 0.2s ease;
  }

  .luqta-modal-close-float:hover {
    background: var(--luqta-border);
  }

  .luqta-modal-close-float svg {
    width: 18px;
    height: 18px;
    color: var(--luqta-text-muted);
  }

  .luqta-level-modal-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 60px 32px 32px;
    text-align: center;
  }

  .luqta-level-type-label {
    display: inline-block;
    padding: 6px 16px;
    background: var(--luqta-error-light);
    color: var(--luqta-error);
    border-radius: 20px;
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 16px;
  }

  .luqta-level-modal-title {
    font-size: 18px;
    font-weight: 600;
    color: var(--luqta-text);
    margin: 0 0 32px;
    line-height: 1.5;
    max-width: 280px;
  }

  /* QR Scanner Area - Figma Design */
  .luqta-qr-scanner-area {
    position: relative;
    width: 200px;
    height: 200px;
    margin: 0 auto 32px;
  }

  .luqta-qr-icon-placeholder {
    width: 100%;
    height: 100%;
    background: var(--luqta-bg-light);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
  }

  .luqta-qr-icon-placeholder svg {
    width: 64px;
    height: 64px;
    color: var(--luqta-text-muted);
  }

  /* Animated rings around QR icon */
  .luqta-qr-icon-placeholder::before,
  .luqta-qr-icon-placeholder::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100%;
    height: 100%;
    border: 2px solid var(--luqta-primary-medium);
    border-radius: 50%;
    transform: translate(-50%, -50%);
    animation: luqta-ring-pulse 2s ease-out infinite;
  }

  .luqta-qr-icon-placeholder::after {
    animation-delay: 1s;
  }

  @keyframes luqta-ring-pulse {
    0% {
      width: 100%;
      height: 100%;
      opacity: 1;
    }
    100% {
      width: 140%;
      height: 140%;
      opacity: 0;
    }
  }

  .luqta-qr-scanner-area .luqta-qr-scanner-preview {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 16px;
    overflow: hidden;
    background: var(--luqta-overlay);
    display: none;
  }

  .luqta-qr-scanner-area .luqta-qr-scanner-preview.active {
    display: block;
  }

  .luqta-qr-scanner-area .luqta-qr-scanner-preview.active + .luqta-qr-icon-placeholder {
    display: none;
  }

  .luqta-qr-scanner-area video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  /* Upload Area - Figma Design */
  .luqta-upload-area {
    width: 100%;
    max-width: 300px;
    height: 200px;
    margin: 0 auto 32px;
    position: relative;
  }

  .luqta-upload-placeholder {
    width: 100%;
    height: 100%;
    border: 2px dashed var(--luqta-border-medium);
    border-radius: 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    background: var(--luqta-bg-light);
  }

  .luqta-upload-placeholder:hover {
    border-color: var(--luqta-primary);
    background: var(--luqta-primary-light);
  }

  .luqta-upload-icon {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .luqta-upload-icon svg {
    width: 48px;
    height: 48px;
    color: var(--luqta-text-light);
  }

  .luqta-upload-hint {
    font-size: 12px;
    color: var(--luqta-text-light);
    margin: 0;
    line-height: 1.6;
    text-align: center;
  }

  .luqta-upload-preview {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 16px;
    overflow: hidden;
    background: var(--luqta-bg-medium);
  }

  .luqta-upload-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  /* Action Button - Figma Design */
  .luqta-level-action-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 16px 48px;
    border: none;
    border-radius: 30px;
    background: linear-gradient(135deg, var(--luqta-primary), var(--luqta-secondary));
    color: white;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: inherit;
    min-width: 200px;
  }

  .luqta-level-action-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px var(--luqta-primary-shadow);
  }

  .luqta-level-action-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  .luqta-level-action-btn svg {
    width: 20px;
    height: 20px;
  }

  /* QR Status in fullscreen modal */
  .luqta-level-modal-content .luqta-qr-status {
    margin-top: 16px;
    padding: 12px 24px;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 500;
  }

  .luqta-level-modal-content .luqta-qr-status:empty {
    display: none;
  }

  .luqta-level-modal-content .luqta-qr-status.scanning {
    background: var(--luqta-primary-light);
    color: var(--luqta-secondary);
  }

  .luqta-level-modal-content .luqta-qr-status.success {
    background: var(--luqta-success-light);
    color: var(--luqta-success);
  }

  .luqta-level-modal-content .luqta-qr-status.error {
    background: var(--luqta-error-light);
    color: var(--luqta-error);
  }

  /* ============================================================================
     CONTEST COMPLETION MODAL
     ============================================================================ */

  .luqta-contest-complete-modal {
    max-width: 420px;
    width: 100%;
    border-radius: 24px;
    overflow: visible;
    background: var(--luqta-bg);
  }

  .luqta-contest-complete-content {
    text-align: center;
    padding: 40px 32px 32px;
    position: relative;
    overflow: visible;
  }

  .luqta-contest-complete-icon {
    width: 80px;
    height: 80px;
    background: linear-gradient(135deg, var(--luqta-warning), var(--luqta-warning-dark));
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    animation: luqta-badge-bounce 0.6s ease;
  }

  .luqta-contest-complete-icon svg {
    width: 40px;
    height: 40px;
    color: white;
  }

  .luqta-contest-complete-title {
    font-size: 24px;
    font-weight: 700;
    margin: 0 0 8px;
    color: var(--luqta-text);
  }

  .luqta-contest-complete-subtitle {
    font-size: 14px;
    color: var(--luqta-text-muted);
    margin: 0 0 8px;
  }

  .luqta-contest-complete-name {
    font-size: 16px;
    font-weight: 600;
    color: var(--luqta-primary);
    margin: 0 0 24px;
  }

  .luqta-contest-complete-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin-bottom: 20px;
  }

  .luqta-contest-complete-stat {
    background: var(--luqta-bg-light);
    padding: 16px 8px;
    border-radius: 12px;
  }

  .luqta-contest-complete-stat-value {
    font-size: 20px;
    font-weight: 700;
    color: var(--luqta-primary);
    margin-bottom: 4px;
  }

  .luqta-contest-complete-stat-label {
    font-size: 11px;
    color: var(--luqta-text-muted);
  }

  .luqta-contest-complete-draw {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 20px;
    background: var(--luqta-warning-light);
    border-radius: 12px;
    margin-bottom: 20px;
    font-size: 14px;
    color: var(--luqta-warning-dark);
  }

  .luqta-contest-complete-draw svg {
    width: 18px;
    height: 18px;
    color: var(--luqta-warning);
  }

  .luqta-contest-complete-prizes {
    margin-bottom: 24px;
  }

  .luqta-contest-complete-prizes-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--luqta-text);
    margin: 0 0 12px;
  }

  .luqta-contest-complete-prizes-list {
    display: flex;
    justify-content: center;
    gap: 16px;
    flex-wrap: wrap;
  }

  .luqta-contest-complete-prize {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }

  .luqta-contest-complete-prize img {
    width: 60px;
    height: 60px;
    border-radius: 12px;
    object-fit: cover;
    border: 2px solid var(--luqta-border);
  }

  .luqta-contest-complete-prize .luqta-prize-placeholder {
    width: 60px;
    height: 60px;
    border-radius: 12px;
    background: linear-gradient(135deg, var(--luqta-warning-light), var(--luqta-warning-light));
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .luqta-contest-complete-prize .luqta-prize-placeholder svg {
    width: 28px;
    height: 28px;
    color: var(--luqta-warning);
  }

  .luqta-contest-complete-prize span {
    font-size: 12px;
    color: var(--luqta-text);
    font-weight: 500;
  }

  .luqta-contest-complete-actions {
    margin-top: 8px;
  }

  /* ============================================================================
     CONTEST INFO BUTTON & MODAL
     ============================================================================ */

  /* Info Button on Contest Card */
  .luqta-contest-info-btn {
    width: 36px;
    height: 36px;
    border: 1px solid var(--luqta-border);
    border-radius: 50%;
    background: var(--luqta-bg);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    flex-shrink: 0;
    position: relative;
    z-index: 10;
  }

  .luqta-contest-info-btn:hover {
    background: var(--luqta-primary-light);
    border-color: var(--luqta-primary);
  }

  .luqta-contest-info-btn svg {
    width: 18px;
    height: 18px;
    color: var(--luqta-text-muted);
    transition: color 0.2s ease;
  }

  .luqta-contest-info-btn:hover svg {
    color: var(--luqta-primary);
  }

  /* Info Modal */
  .luqta-info-modal {
    max-width: 400px;
  }

  .luqta-info-modal-large {
    max-width: 520px;
  }

  .luqta-info-content {
    padding: 4px 0;
  }

  .luqta-info-header {
    margin-bottom: 20px;
  }

  .luqta-info-title {
    font-size: 20px;
    font-weight: 700;
    margin: 0 0 8px;
    color: var(--luqta-text);
  }

  .luqta-info-desc {
    font-size: 14px;
    color: var(--luqta-text-muted);
    line-height: 1.6;
    margin: 0;
  }

  /* Info Grid */
  .luqta-info-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    margin-bottom: 20px;
  }

  .luqta-info-item {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px;
    background: var(--luqta-bg-light);
    border-radius: 10px;
  }

  .luqta-info-icon {
    flex-shrink: 0;
    color: var(--luqta-primary);
  }

  .luqta-info-icon svg {
    width: 18px;
    height: 18px;
  }

  .luqta-info-detail {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .luqta-info-label {
    font-size: 11px;
    color: var(--luqta-text-light);
    font-weight: 500;
    text-transform: uppercase;
  }

  .luqta-info-value {
    font-size: 13px;
    color: var(--luqta-text);
    font-weight: 600;
    word-break: break-word;
  }

  /* Info Stats */
  .luqta-info-stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
    margin-bottom: 20px;
  }

  .luqta-info-stat {
    text-align: center;
    padding: 12px 8px;
    background: linear-gradient(135deg, var(--luqta-primary-light) 0%, var(--luqta-primary-light) 100%);
    border-radius: 10px;
  }

  .luqta-info-stat-value {
    font-size: 20px;
    font-weight: 700;
    color: var(--luqta-primary);
    display: block;
  }

  .luqta-info-stat-label {
    font-size: 10px;
    color: var(--luqta-text-muted);
    font-weight: 500;
    display: block;
    margin-top: 4px;
  }

  /* Info Badges */
  .luqta-info-badges {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 20px;
  }

  .luqta-info-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 16px;
    font-size: 12px;
    font-weight: 500;
    background: var(--luqta-bg-medium);
    color: var(--luqta-text);
  }

  .luqta-info-badge svg {
    width: 14px;
    height: 14px;
  }

  .luqta-info-badge.public {
    background: var(--luqta-success-light);
    color: var(--luqta-success-dark);
  }

  .luqta-info-badge.private {
    background: var(--luqta-warning-light);
    color: var(--luqta-warning-dark);
  }

  .luqta-info-badge.view-type {
    background: var(--luqta-primary-light);
    color: var(--luqta-secondary);
  }

  /* Info Sections */
  .luqta-info-section {
    margin-bottom: 16px;
    padding-top: 16px;
    border-top: 1px solid var(--luqta-border);
  }

  .luqta-info-section-title {
    font-size: 14px;
    font-weight: 600;
    margin: 0 0 12px;
    color: var(--luqta-text);
  }

  /* Levels Preview */
  .luqta-info-levels-preview {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .luqta-info-level-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    background: var(--luqta-bg-light);
    border-radius: 8px;
  }

  .luqta-info-level-icon {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: var(--luqta-primary-light);
  }

  .luqta-info-level-icon svg {
    width: 16px;
    height: 16px;
    color: var(--luqta-primary);
  }

  .luqta-info-level-icon.text {
    background: var(--luqta-primary-light);
  }
  .luqta-info-level-icon.text svg {
    color: var(--luqta-secondary);
  }

  .luqta-info-level-icon.qr {
    background: var(--luqta-success-light);
  }
  .luqta-info-level-icon.qr svg {
    color: var(--luqta-success);
  }

  .luqta-info-level-icon.link {
    background: var(--luqta-primary-light);
  }
  .luqta-info-level-icon.link svg {
    color: var(--luqta-info);
  }

  .luqta-info-level-icon.image {
    background: var(--luqta-warning-light);
  }
  .luqta-info-level-icon.image svg {
    color: var(--luqta-warning);
  }

  .luqta-info-level-icon.quiz {
    background: var(--luqta-primary-light);
  }
  .luqta-info-level-icon.quiz svg {
    color: var(--luqta-primary);
  }

  .luqta-info-level-name {
    flex: 1;
    font-size: 13px;
    font-weight: 500;
    color: var(--luqta-text);
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .luqta-info-level-points {
    font-size: 12px;
    font-weight: 600;
    color: var(--luqta-primary);
    flex-shrink: 0;
  }

  .luqta-info-more {
    text-align: center;
    font-size: 12px;
    color: var(--luqta-text-light);
    padding: 8px;
  }

  /* Prizes Preview */
  .luqta-info-prizes-preview {
    display: flex;
    gap: 12px;
    overflow-x: auto;
    padding-bottom: 4px;
    -webkit-overflow-scrolling: touch;
  }

  .luqta-info-prizes-preview::-webkit-scrollbar {
    display: none;
  }

  .luqta-info-prize-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

  .luqta-info-prize-img {
    width: 56px;
    height: 56px;
    border-radius: 10px;
    object-fit: cover;
    border: 2px solid var(--luqta-border);
  }

  .luqta-info-prize-placeholder {
    width: 56px;
    height: 56px;
    border-radius: 10px;
    background: linear-gradient(135deg, var(--luqta-warning-light), var(--luqta-warning-light));
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .luqta-info-prize-placeholder svg {
    width: 24px;
    height: 24px;
    color: var(--luqta-warning);
  }

  .luqta-info-prize-name {
    font-size: 11px;
    color: var(--luqta-text);
    font-weight: 500;
    text-align: center;
    max-width: 70px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Draw Rules */
  .luqta-info-rules {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .luqta-info-rule {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: var(--luqta-text);
  }

  .luqta-info-rule svg {
    width: 16px;
    height: 16px;
    color: var(--luqta-success);
    flex-shrink: 0;
  }

  /* Access Code Modal Content */
  .luqta-access-code-content {
    text-align: center;
    padding: 16px 0;
  }

  .luqta-access-code-icon {
    width: 56px;
    height: 56px;
    margin: 0 auto 16px;
    background: var(--luqta-warning-light);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .luqta-access-code-icon svg {
    width: 28px;
    height: 28px;
    color: var(--luqta-warning);
  }

  .luqta-access-code-desc {
    font-size: 14px;
    color: var(--luqta-text-muted);
    margin: 0 0 20px;
    line-height: 1.6;
  }

  /* ============================================================================
     CONTEST INFO PAGE (Full Page View)
     ============================================================================ */

  .luqta-contest-info-page {
    background: var(--luqta-bg-light);
    min-height: 100%;
  }

  .luqta-info-page-content {
    padding: 20px;
  }

  .luqta-info-page-header {
    background: var(--luqta-bg);
    border-radius: 16px;
    padding: 20px;
    margin-bottom: 16px;
  }

  .luqta-info-page-title {
    font-size: 22px;
    font-weight: 700;
    margin: 0 0 10px;
    color: var(--luqta-text);
    line-height: 1.3;
  }

  .luqta-info-page-desc {
    font-size: 14px;
    color: var(--luqta-text-muted);
    line-height: 1.6;
    margin: 0 0 16px;
  }

  .luqta-info-page-section {
    background: var(--luqta-bg);
    border-radius: 16px;
    padding: 20px;
    margin-bottom: 16px;
  }

  .luqta-info-page-section-title {
    font-size: 16px;
    font-weight: 700;
    margin: 0 0 16px;
    color: var(--luqta-text);
  }

  /* Info Page Levels List */
  .luqta-info-levels-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .luqta-info-level-card {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 16px;
    background: var(--luqta-bg-light);
    border-radius: 12px;
    border: 1px solid var(--luqta-border);
  }

  .luqta-info-level-info {
    flex: 1;
    min-width: 0;
  }

  .luqta-info-level-title {
    font-size: 15px;
    font-weight: 600;
    margin: 0 0 4px;
    color: var(--luqta-text);
  }

  .luqta-info-level-desc {
    font-size: 13px;
    color: var(--luqta-text-muted);
    margin: 0;
    line-height: 1.5;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .luqta-info-level-points-badge {
    flex-shrink: 0;
    padding: 6px 12px;
    background: linear-gradient(135deg, var(--luqta-primary-light) 0%, var(--luqta-primary-medium) 100%);
    color: var(--luqta-primary);
    font-size: 13px;
    font-weight: 700;
    border-radius: 16px;
  }

  /* Draw Rules Card */
  .luqta-info-rules-card {
    background: var(--luqta-bg-light);
    border-radius: 12px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  /* Prize Value */
  .luqta-detail-prize-value {
    display: inline-block;
    margin-top: 8px;
    padding: 4px 10px;
    background: linear-gradient(135deg, var(--luqta-warning-light), var(--luqta-warning-light));
    color: var(--luqta-warning-dark);
    font-size: 12px;
    font-weight: 600;
    border-radius: 12px;
  }

  /* Info Page Footer (Fixed Bottom Button) */
  .luqta-info-page-footer {
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 16px 20px;
    background: var(--luqta-bg);
    border-top: 1px solid var(--luqta-border);
    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.08);
  }

  /* Progress Section */
  .luqta-progress-section {
    background: linear-gradient(135deg, var(--luqta-primary-light) 0%, var(--luqta-primary-light) 100%);
  }

  .luqta-info-progress-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }

  .luqta-info-progress-text {
    font-size: 14px;
    font-weight: 600;
    color: var(--luqta-primary);
  }

  .luqta-info-progress-stats {
    display: flex;
    justify-content: space-between;
    margin-top: 12px;
  }

  .luqta-info-progress-points {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    font-weight: 600;
    color: var(--luqta-primary);
  }

  .luqta-info-progress-points svg {
    width: 16px;
    height: 16px;
  }

  /* Participating Badge */
  .luqta-info-badge.participating {
    background: var(--luqta-success-light);
    color: var(--luqta-success-dark);
  }

  .luqta-info-badge.participating svg {
    width: 12px;
    height: 12px;
  }

  /* Level Card States */
  .luqta-info-level-card.clickable {
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .luqta-info-level-card.clickable:hover {
    border-color: var(--luqta-primary);
    background: var(--luqta-primary-light);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px var(--luqta-primary-medium);
  }

  .luqta-info-level-card.completed {
    background: var(--luqta-success-lighter);
    border-color: var(--luqta-success);
  }

  .luqta-info-level-card.locked {
    opacity: 0.6;
  }

  .luqta-info-level-icon.completed {
    background: var(--luqta-success) \!important;
  }

  .luqta-info-level-icon.completed svg {
    color: white !important;
  }

  /* Level Status Labels */
  .luqta-info-level-status {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    font-weight: 500;
    margin-top: 4px;
  }

  .luqta-info-level-status.completed {
    color: var(--luqta-success);
  }

  .luqta-info-level-status.locked {
    color: var(--luqta-text-light);
  }

  .luqta-info-level-status svg {
    width: 12px;
    height: 12px;
  }

  /* Level Action Area */
  .luqta-info-level-action {
    flex-shrink: 0;
    display: flex;
    align-items: center;
  }

  .luqta-info-level-play-btn {
    padding: 8px 16px;
    background: var(--luqta-primary);
    color: white;
    border: none;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .luqta-info-level-play-btn:hover {
    background: var(--luqta-primary-dark);
    transform: scale(1.05);
  }

  .luqta-info-level-check {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: var(--luqta-success);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .luqta-info-level-check svg {
    width: 18px;
    height: 18px;
    color: white;
  }

  /* Small Spinner for Button Loading */
  .luqta-spinner-small {
    display: inline-block;
    width: 16px;
    height: 16px;
    border: 2px solid var(--luqta-inverted-border);
    border-radius: 50%;
    border-top-color: white;
    animation: luqta-spin 0.8s linear infinite;
    margin-right: 8px;
    vertical-align: middle;
  }

  /* Responsive */
  @media (max-width: 640px) {
    .luqta-contests-grid {
      grid-template-columns: 1fr;
    }

    .luqta-contest-stats {
      grid-template-columns: 1fr;
    }

    .luqta-modal {
      margin: 10px;
      max-height: calc(100vh - 20px);
    }

    .luqta-nav {
      padding: 12px 16px;
    }

    .luqta-nav-btn {
      padding: 8px 16px;
      font-size: 13px;
    }

    .luqta-detail-banner {
      height: 220px;
    }

    .luqta-detail-company-card {
      width: 140px;
    }

    .luqta-detail-prize-card {
      width: 120px;
    }
  }

  .luqta-watermark {
    text-align: center;
    padding: 8px 0 12px;
    font-size: 11px;
    color: #999;
    font-weight: 500;
    pointer-events: none;
    user-select: none;
  }

  /* ============================================================
     PROFILE ICON (draggable floating button)
     ============================================================ */

  .luqta-profile-icon {
    position: absolute;
    right: 16px;
    bottom: 100px;
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--luqta-primary, #5304fb), rgba(83,4,251,0.75));
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    box-shadow: 0 3px 8px rgba(83,4,251,0.35);
    z-index: 100;
    touch-action: none;
    user-select: none;
    transition: box-shadow 0.15s;
  }

  .luqta-profile-icon:hover {
    box-shadow: 0 5px 14px rgba(83,4,251,0.45);
  }

  /* ============================================================
     PROFILE SHEET
     ============================================================ */

  .luqta-profile-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.4);
    z-index: 9999;
    display: flex;
    align-items: flex-end;
    justify-content: center;
  }

  .luqta-profile-sheet {
    background: #fff;
    border-radius: 24px 24px 0 0;
    width: 100%;
    max-width: 480px;
    padding-bottom: env(safe-area-inset-bottom, 16px);
    max-height: 90vh;
    overflow-y: auto;
  }

  .luqta-profile-handle {
    width: 36px;
    height: 4px;
    background: #e5e7eb;
    border-radius: 2px;
    margin: 12px auto 8px;
  }

  .luqta-profile-header {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 12px 20px;
  }

  .luqta-profile-avatar {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--luqta-profile-primary, #5304fb), rgba(83,4,251,0.7));
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    flex-shrink: 0;
  }

  .luqta-profile-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .luqta-profile-name {
    font-size: 18px;
    font-weight: 700;
    color: #111827;
  }

  .luqta-profile-contact {
    font-size: 13px;
    color: #6b7280;
  }

  .luqta-profile-verified {
    font-size: 11px;
    font-weight: 600;
    color: #10b981;
    background: rgba(16,185,129,0.1);
    padding: 3px 8px;
    border-radius: 12px;
    white-space: nowrap;
  }

  .luqta-profile-divider {
    border: none;
    border-top: 1px solid #f3f4f6;
    margin: 0;
  }

  .luqta-profile-points-badge {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 14px 20px;
    padding: 12px 16px;
    background: rgba(83,4,251,0.08);
    border-radius: 14px;
    border: 1px solid rgba(83,4,251,0.15);
  }

  .luqta-profile-points-value {
    font-size: 22px;
    font-weight: 800;
    color: var(--luqta-profile-primary, #5304fb);
  }

  .luqta-profile-points-label {
    font-size: 12px;
    color: #6b7280;
  }

  .luqta-profile-stats-title {
    font-size: 15px;
    font-weight: 700;
    color: #111827;
    margin: 4px 20px 8px;
  }

  .luqta-profile-stats-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    padding: 0 20px 16px;
  }

  .luqta-profile-stat-card {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 10px 12px;
    background: rgba(83,4,251,0.06);
    border-radius: 12px;
    border: 1px solid rgba(83,4,251,0.12);
  }

  .luqta-profile-stat-value {
    font-size: 16px;
    font-weight: 700;
    color: var(--luqta-profile-primary, #5304fb);
  }

  .luqta-profile-stat-label {
    font-size: 10px;
    color: #6b7280;
  }
`;

/**
 * Convert hex color to rgba
 */
function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Check if a color is dark based on luminance
 */
function isColorDark(hex: string): boolean {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.5;
}

/**
 * Inject styles into document
 */
export function injectStyles(branding: BrandingConfig = {}): void {
  const existingStyle = document.getElementById('luqta-sdk-styles');
  if (existingStyle) {
    existingStyle.remove();
  }

  // Generate CSS variables from branding config
  const cssVariables = generateCSSVariables(branding);

  // Create styles with CSS variables scoped to .luqta-sdk
  const stylesWithVariables = `
    .luqta-sdk {
      ${cssVariables}
    }
    ${SDK_STYLES}
  `;

  const styleElement = document.createElement('style');
  styleElement.id = 'luqta-sdk-styles';
  styleElement.textContent = stylesWithVariables;
  document.head.appendChild(styleElement);
}
